import { Link, useParams } from 'react-router-dom';
import { getPayment, getPaymentAttempts, getTransaction } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { formatDateTime, statusLabel } from '@/services/formatters';

/**
 * Transaction detail — WF-SMEM-04.
 *
 * A failed or reversed transaction is presented with its reason and the money
 * position stated plainly, because the most urgent question a user has is
 * "where is my money". The attempt timeline is shown from the related payment,
 * so a retry reads as a second attempt rather than a mutated status.
 */
export function TransactionDetailPage() {
  const { transactionId = '' } = useParams();
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const transaction = useAsync(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await getTransaction(companyId, userId, transactionId)).data;
  }, [companyId, userId, transactionId]);

  const relatedPaymentId = transaction.data?.relatedPaymentId ?? null;

  const payment = useAsync(async () => {
    if (!companyId || !relatedPaymentId) return null;
    return (await getPayment(companyId, relatedPaymentId)).data;
  }, [companyId, relatedPaymentId]);

  const attempts = useAsync(async () => {
    if (!relatedPaymentId) return [];
    return (await getPaymentAttempts(relatedPaymentId)).data;
  }, [relatedPaymentId]);

  if (!resolved) return null;

  if (transaction.status === 'loading') {
    return (
      <>
        <MobileHeader title="Transaction" backTo="/mobile/transactions" backLabel="Transactions" />
        <LoadingRegion label="Loading transaction">
          <div className="tm-stack">
            <CardSkeleton height={150} />
            <CardSkeleton height={190} />
          </div>
        </LoadingRegion>
      </>
    );
  }

  if (transaction.status === 'error') {
    return (
      <>
        <MobileHeader title="Transaction" backTo="/mobile/transactions" backLabel="Transactions" />
        <ErrorState
          error={transaction.error}
          onRetry={transaction.reload}
          title="We could not load this transaction"
        />
      </>
    );
  }

  const tx = transaction.data;
  const inbound = tx.direction === 'in';

  const typeLabel: Record<string, string> = {
    'bank-transfer': 'Bank transfer',
    'card-purchase': 'Card payment',
    'direct-debit': 'Direct Debit',
    fx: 'Currency exchange',
    fee: 'Fee',
  };

  return (
    <>
      <MobileHeader title="Transaction" backTo="/mobile/transactions" backLabel="Transactions" />

      <div className="tm-stack">
        <div className="tm-card" style={{ textAlign: 'center' }}>
          <p className="tm-eyebrow">{inbound ? 'Money in from' : 'Money out to'}</p>
          <h2 className="tm-page-title" style={{ marginBlockEnd: 8 }}>
            {tx.counterparty}
          </h2>
          <Amount
            minorUnits={tx.amount}
            currency={tx.currency}
            size="hero"
            signed
            direction={tx.direction}
          />
          <div style={{ marginBlockStart: 10 }}>
            <StatusBadge status={tx.status} />
          </div>
        </div>

        {/* Failed and reversed presentation states where the money is. */}
        {tx.status === 'failed' ? (
          <Notice tone="danger" title="This payment failed">
            {payment.data?.failureReason ??
              'The payment could not be completed.'}{' '}
            No money left your account.
          </Notice>
        ) : null}

        {tx.status === 'reversed' ? (
          <Notice tone="warning" title="This payment was returned">
            {payment.data?.failureReason ?? 'The receiving bank returned the payment.'} The amount
            has been credited back to your account.
          </Notice>
        ) : null}

        {tx.status === 'pending' ? (
          <Notice tone="info" title="Still processing">
            This transaction has not settled yet. The amount is reserved and is not part of your
            available balance.
          </Notice>
        ) : null}

        <section aria-labelledby="tx-details" className="tm-card">
          <SectionHeader title="Details" id="tx-details" />
          <div style={{ marginBlockStart: 12 }}>
            <DetailList
              items={[
                { label: 'Type', value: typeLabel[tx.type] ?? statusLabel(tx.type) },
                { label: 'Status', value: <StatusBadge status={tx.status} /> },
                { label: 'Date', value: formatDateTime(tx.occurredAt) },
                ...(tx.bookedAt
                  ? [{ label: 'Booked', value: formatDateTime(tx.bookedAt) }]
                  : []),
                ...(tx.reference ? [{ label: 'Reference', value: tx.reference }] : []),
                ...(tx.merchantName ? [{ label: 'Merchant', value: tx.merchantName }] : []),
                ...(tx.category ? [{ label: 'Category', value: tx.category }] : []),
                ...(tx.fxRateApplied
                  ? [{ label: 'Rate applied', value: tx.fxRateApplied.toFixed(4) }]
                  : []),
                ...(tx.feeAmount
                  ? [
                      {
                        label: 'Fee',
                        value: <Amount minorUnits={tx.feeAmount} currency={tx.currency} />,
                      },
                    ]
                  : []),
                { label: 'Transaction ID', value: tx.id },
              ]}
            />
          </div>
        </section>

        {/* Supporting context: where this transaction came from. */}
        <section aria-labelledby="tx-context">
          <SectionHeader title="Related" id="tx-context" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <ListRowLink
                  to={`/mobile/accounts/${tx.accountId}`}
                  title="Account"
                  meta="See this account and its details"
                />
              </li>
              {tx.relatedCardId ? (
                <li>
                  <ListRowLink
                    to={`/mobile/cards/${tx.relatedCardId}`}
                    title="Card used"
                    meta="Open the card this was charged to"
                  />
                </li>
              ) : null}
              {relatedPaymentId ? (
                <li>
                  <ListRowLink
                    to={`/mobile/payments/${relatedPaymentId}`}
                    title="Payment record"
                    meta="See the payment and its approval history"
                  />
                </li>
              ) : null}
            </ul>
          </div>
        </section>

        {/* Attempt timeline: a retry is a new attempt, never a rewritten status. */}
        {(attempts.data ?? []).length > 0 ? (
          <section aria-labelledby="tx-timeline">
            <SectionHeader title="Timeline" id="tx-timeline" />
            <div className="tm-card">
              <ol className="tm-timeline">
                {(attempts.data ?? []).map((attempt) => (
                  <li key={attempt.id} className="tm-timeline__item">
                    <div className="tm-between">
                      <strong>Attempt {attempt.attemptNumber}</strong>
                      <StatusBadge status={attempt.status} />
                    </div>
                    <p className="tm-hint" style={{ margin: '2px 0 0' }}>
                      {formatDateTime(attempt.startedAt)}
                      {attempt.providerReference ? ` · ${attempt.providerReference}` : ''}
                    </p>
                    {attempt.failureReason ? (
                      <p className="tm-muted" style={{ margin: '4px 0 0' }}>
                        {attempt.failureReason}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        ) : null}

        <Link to="/mobile/continue-on-web?task=exports" className="tm-btn tm-btn--secondary tm-btn--block">
          Download a statement on web
        </Link>
      </div>
    </>
  );
}

function ListRowLink({ to, title, meta }: { to: string; title: string; meta: string }) {
  return (
    <Link className="tm-row" to={to}>
      <span className="tm-row__body">
        <span className="tm-row__title">{title}</span>
        <span className="tm-row__meta">{meta}</span>
      </span>
      <svg
        className="tm-row__chevron"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}
